// File: src/components/MarketplaceSelectionDialog.tsx
// FINAL CORRECTED VERSION. The label no longer uses the non-existent 'country' field.

import React, {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {getMarketplaces} from '../services/api';

interface MarketplaceSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (marketplaceId: number) => void;
    productName: string;
    loading?: boolean;
}

const MarketplaceSelectionDialog: React.FC<MarketplaceSelectionDialogProps> = ({
                                                                                   open,
                                                                                   onClose,
                                                                                   onSelect,
                                                                                   productName,
                                                                                   loading = false
                                                                               }) => {
    const {t} = useTranslation();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const {data: marketplaces = [], isLoading: isLoadingMarketplaces, error} = useQuery({
        queryKey: ['marketplaces'],
        // We now filter by is_active on the frontend, assuming it's not a direct DB field
        queryFn: () => getMarketplaces(),
        enabled: open,
    });

    useEffect(() => {
        if (open && marketplaces.length > 0 && !selectedId) {
            setSelectedId(marketplaces[0].id);
        } else if (!open) {
            setSelectedId(null);
        }
    }, [open, marketplaces, selectedId]);

    const handleSelect = () => {
        if (selectedId) {
            onSelect(selectedId);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>
                {t('marketplace_selection.title', { productName })}
            </DialogTitle>
            <DialogContent dividers>
                <>
                    {error && (
                        <Alert severity="error">
                            {(error as Error).message}
                        </Alert>
                    )}
                    {isLoadingMarketplaces ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <RadioGroup value={selectedId} onChange={(e) => setSelectedId(Number(e.target.value))}>
                            {marketplaces.map((marketplace) => (
                                <FormControlLabel
                                    key={marketplace.id}
                                    value={marketplace.id}
                                    control={<Radio />}
                                    label={marketplace.name}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        p: 1,
                                        mb: 1,
                                        ml: 0
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    )}
                </>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t('marketplace_selection.cancel')}
                </Button>
                <Button
                    onClick={handleSelect}
                    variant="contained"
                    disabled={loading || isLoadingMarketplaces || !selectedId}
                >
                    {t('marketplace_selection.create_project')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarketplaceSelectionDialog;
